import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import ComplianceAIForm from "@/components/compliance-ai/ComplianceAIForm";
import ComplianceAIResponse from "@/components/compliance-ai/ComplianceAIResponse";
import { ComplianceFormData } from "@/types/compliance";
import { mockComplianceAiResponse } from "@/mocks/complianceAiResponse";
import { API_CONFIG } from "@/constants/env.ts";
import { 
  CheckCircle, 
  Clock, 
  FileCheck, 
  BarChart3, 
  Shield, 
  Briefcase, 
  Brain, 
  Bot, 
  Code, 
  MessageSquare,
  Layers,
  Lock,
  Server
} from "lucide-react";

const LuthorAILanding = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);

    try {
      // Use the actual API
      const apiUrl = `${window.location.origin}/compliance-ai`;
      console.log("Sending request to:", apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
      }
      
      let responseData = await response.json();
      
      console.log("API Response:", responseData);
      
      // Process the response if needed
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (e) {
          responseData = { content: responseData };
        }
      }
      
      setResponse(responseData);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error instanceof Error) {
        console.log('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }
      
      // Fallback to mock data if API fails
      if (API_CONFIG.USE_MOCK_API) {
        console.log("Falling back to mock data due to API error");
        setTimeout(() => {
          setResponse({
            ...mockComplianceAiResponse,
            title: "AI Agent Technology Assessment",
            summary: "Your custom AI agent requirements have been analyzed. Here are our recommendations for implementation."
          });
        }, 1000);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="relative min-h-screen overflow-hidden">
      <Helmet>
        <title>Enterprise-Grade AI Agents | Luthor AI</title>
        <meta 
          name="description" 
          content="Build enterprise-grade AI agents with Luthor AI. Create intelligent assistants that can integrate with your business tools and processes." 
        />
      </Helmet>
      
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-gray-50">
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(124, 58, 237, 0.4) 0%, rgba(124, 58, 237, 0) 70%)',
            transform: 'translate(30%, -30%)'
          }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(124, 58, 237, 0.3) 0%, rgba(124, 58, 237, 0) 70%)',
            transform: 'translate(-30%, 30%)'
          }}
        ></div>
      </div>
      
      {/* Navbar - landing mode hides platform, tools and login buttons */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm">
        <NavBar landingMode={true} />
      </header>
      
      <main className="relative z-10 pt-20">
        {/* Main content with form section */}
        <section className="py-12 pb-16 bg-transparent">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto text-center mb-8">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 tracking-tight">
                <span className="block">Enterprise-Grade</span>
                <span className="bg-gradient-to-r from-purple-600 to-indigo-500 bg-clip-text text-transparent">AI Agents Platform</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
                Build intelligent AI agents that integrate with your business tools, follow your processes, and deliver exceptional results to your customers.
              </p>
              <div className="flex flex-wrap justify-center items-center gap-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-5 w-5 text-purple-500" />
                  <span>Free Agent Assessment</span>
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-5 w-5 text-purple-500" />
                  <span>Deploy in Days, Not Months</span>
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FileCheck className="h-5 w-5 text-purple-500" />
                  <span>Enterprise Security & Compliance</span>
                </div>
              </div>
            </div>
            
            {/* Platform Screenshot */}
            <div className="max-w-5xl mx-auto mb-16">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="rounded-xl overflow-hidden shadow-2xl border border-gray-200"
              >
                <img 
                  src="/screens/dashboard_overview.png" 
                  alt="Luthor AI Platform Dashboard" 
                  className="w-full h-auto"
                />
              </motion.div>
            </div>
            
            {/* Form Section */}
            <div className="max-w-5xl mx-auto mb-16">
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200/80">
                {!response ? (
                  <>
                    <h2 className="text-2xl font-bold mb-6 text-center">Tell us about your AI agent requirements</h2>
                    <ComplianceAIForm 
                      onSubmit={handleFormSubmit} 
                      isLoading={isLoading}
                      error={error}
                    />
                  </>
                ) : (
                  <ComplianceAIResponse 
                    response={response} 
                    isLoading={isLoading} 
                    error={error}
                    onReset={() => {
                      setFormData(null);
                      setResponse(null);
                      setError(null);
                    }} 
                  />
                )}
              </div>
            </div>
          </div>
        </section>
        
        {/* Key Features Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Powerful AI Agent Capabilities</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Build AI agents that understand your business, follow your processes, and integrate with your tools.
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {[
                  { name: "Tool Integration", description: "Seamlessly connect to your existing business tools" },
                  { name: "Customizable Workflows", description: "Design agent processes that match your business needs" },
                  { name: "Multi-Agent Systems", description: "Create teams of specialized AI agents that work together" },
                  { name: "Human-in-the-Loop", description: "Smooth handoff between AI and human operators" },
                  { name: "Contextual Memory", description: "Agents remember previous interactions and user preferences" },
                  { name: "Enterprise Security", description: "GDPR compliance with end-to-end encryption" },
                  { name: "Dynamic Learning", description: "Agents improve through feedback and continuous learning" },
                  { name: "Performance Analytics", description: "Comprehensive metrics and usage analytics" }
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="bg-purple-50 p-4 rounded-xl border border-purple-100"
                  >
                    <h3 className="font-bold text-purple-700 mb-1">{feature.name}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Platform Features Section with Screenshots */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Enterprise-Grade AI Agent Platform</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Our platform provides everything you need to build, deploy, and scale AI agents for your business.
                </p>
              </div>
              
              {/* Feature 1 - Agent Builder */}
              <div className="mb-16 grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <h3 className="text-2xl font-bold mb-4 text-purple-700">Intuitive Agent Builder</h3>
                  <p className="text-lg text-gray-600 mb-4">
                    Create powerful AI agents without code using our intuitive builder. Define agent capabilities, personality, knowledge, and processes with a simple interface.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>Visual workflow designer for agent processes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>Integrate third-party tools and APIs with ease</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>Pre-built templates for common agent types</span>
                    </li>
                  </ul>
                </div>
                <div className="order-1 md:order-2">
                  <img 
                    src="/screens/user_profile.png" 
                    alt="Luthor AI Agent Builder" 
                    className="rounded-xl shadow-lg border border-gray-200"
                  />
                </div>
              </div>
              
              {/* Feature 2 - Knowledge Base */}
              <div className="mb-16 grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <img 
                    src="/screens/aifmd/aifguard_1.png" 
                    alt="Luthor AI Knowledge Management" 
                    className="rounded-xl shadow-lg border border-gray-200"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4 text-purple-700">Enterprise Knowledge Integration</h3>
                  <p className="text-lg text-gray-600 mb-4">
                    Connect your agents to your enterprise data sources to provide them with the context they need to serve your customers effectively.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>Secure integration with document repositories</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>Automatic knowledge base updates and syncing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>Custom retrieval and relevance mechanisms</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Feature 3 - Analytics */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <h3 className="text-2xl font-bold mb-4 text-purple-700">Comprehensive Analytics</h3>
                  <p className="text-lg text-gray-600 mb-4">
                    Gain insights into your agents' performance and user interactions with detailed analytics dashboards.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>Track agent usage, performance, and outcomes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>Identify improvement opportunities with AI suggestions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <span>Exportable reports for stakeholder review</span>
                    </li>
                  </ul>
                </div>
                <div className="order-1 md:order-2">
                  <img 
                    src="/screens/aifmd/aifguard_2.png" 
                    alt="Luthor AI Analytics Dashboard" 
                    className="rounded-xl shadow-lg border border-gray-200"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* How Luthor AI helps section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">AI Agent Applications</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Our platform enables a wide range of AI agent applications across industries and use cases.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    title: "Customer Support Automation",
                    description: "Build AI agents that resolve customer inquiries instantly while seamlessly escalating complex issues to human agents.",
                    icon: <MessageSquare className="h-10 w-10 text-purple-500 mb-4" />
                  },
                  {
                    title: "Knowledge Workers",
                    description: "Create AI assistants that help knowledge workers find information, generate content, and automate routine tasks.",
                    icon: <Brain className="h-10 w-10 text-purple-500 mb-4" />
                  },
                  {
                    title: "Workflow Automation",
                    description: "Deploy agents that coordinate complex business processes, making decisions and taking actions across multiple systems.",
                    icon: <Layers className="h-10 w-10 text-purple-500 mb-4" />
                  }
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-md border border-gray-200"
                  >
                    {feature.icon}
                    <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Enterprise Security Section */}
        <section className="py-16 bg-purple-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Enterprise-Grade Security</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Luthor AI is built with enterprise security and compliance at its core.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    title: "GDPR compliance",
                    description: "Our platform adheres to SOC 2 standards, ensuring your data is handled with the highest security and privacy standards.",
                    icon: <Shield className="h-10 w-10 text-purple-500 mb-4" />
                  },
                  {
                    title: "Private Deployment",
                    description: "Deploy Luthor AI in your own cloud environment or on-premises for complete control over your data and infrastructure.",
                    icon: <Server className="h-10 w-10 text-purple-500 mb-4" />
                  },
                  {
                    title: "End-to-End Encryption",
                    description: "All data is encrypted in transit and at rest, ensuring your sensitive information is always protected.",
                    icon: <Lock className="h-10 w-10 text-purple-500 mb-4" />
                  }
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-md"
                  >
                    {feature.icon}
                    <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Strategic Partnership section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Trusted by Industry Leaders</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Leading companies across industries trust Luthor AI to power their intelligent agents.
                </p>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="md:w-1/3">
                    <img 
                      src="/logos/amazon.png" 
                      alt="Amazon Logo" 
                      className="h-16 mx-auto md:mx-0"
                    />
                  </div>
                  <div className="md:w-2/3">
                    <h3 className="text-xl font-bold text-purple-600 mb-2">Enterprise AI Transformation</h3>
                    <p className="text-gray-700 mb-4">
                      "Luthor AI has transformed how we approach customer service automation. Their platform enabled us to deploy
                      intelligent agents that handle 78% of customer inquiries without human intervention, while maintaining a 
                      96% customer satisfaction rating."
                    </p>
                    <p className="font-medium">- Sarah Johnson, VP of Customer Experience</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-12 flex flex-wrap gap-8 items-center justify-center">
                <img src="/logos/4game.jpg" alt="Partner Logo" className="h-8 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/circle8.webp" alt="Partner Logo" className="h-8 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/dutchkernmit.png" alt="Partner Logo" className="h-8 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/ei.png" alt="Partner Logo" className="h-8 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
              </div>
            </div>
          </div>
        </section>
        
        {/* Features & Benefits section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Key Benefits</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Why enterprises choose Luthor AI for their intelligent agent needs
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    title: "Rapid Deployment",
                    description: "Deploy powerful AI agents in days, not months, with our no-code builder and pre-built templates.",
                    icon: <Clock className="h-6 w-6 text-purple-500" />
                  },
                  {
                    title: "Seamless Integration",
                    description: "Integrate with your existing enterprise systems, from CRM to ERP, with our extensive API library.",
                    icon: <Code className="h-6 w-6 text-purple-500" />
                  },
                  {
                    title: "Enterprise Control",
                    description: "Maintain complete control over your AI agents with robust governance, oversight, and analytics tools.",
                    icon: <Shield className="h-6 w-6 text-purple-500" />
                  },
                  {
                    title: "Continuous Improvement",
                    description: "Our platform learns from interactions and feedback to continuously improve agent performance over time.",
                    icon: <Bot className="h-6 w-6 text-purple-500" />
                  }
                ].map((benefit, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl flex items-start gap-4 border border-gray-200"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Common questions about our enterprise AI agent platform
                </p>
              </div>
              
              <div className="space-y-4">
                {[
                  {
                    question: "How does Luthor AI compare to other AI agent platforms?",
                    answer: "Luthor AI is designed specifically for enterprise needs, with robust security, governance, and integration capabilities. Our platform offers greater customization, enterprise-grade security, and more powerful tool integration than consumer-grade alternatives."
                  },
                  {
                    question: "Can I customize the AI models that power my agents?",
                    answer: "Yes, Luthor AI offers model flexibility. You can choose from leading foundation models, fine-tune models on your data, or even bring your own custom models to the platform."
                  },
                  {
                    question: "How does the pricing work?",
                    answer: "We offer flexible pricing based on the number of agents, usage volume, and enterprise features required. Our team can provide a custom quote based on your specific needs and use cases."
                  },
                  {
                    question: "Can Luthor AI deploy on-premises or in private clouds?",
                    answer: "Yes, we offer both cloud-hosted options and private deployments. Our platform can be deployed in your AWS, Azure, or GCP environment, or even on-premises for industries with strict data sovereignty requirements."
                  }
                ].map((faq, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-200"
                  >
                    <h3 className="text-xl font-bold mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Final CTA */}
        <section className="py-12 bg-purple-600 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4">Transform Your Business with Intelligent AI Agents</h2>
              <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
                Get your free AI agent assessment today and discover how Luthor AI can help your enterprise build powerful, intelligent assistants.
              </p>
              <Button 
                size="lg" 
                className="bg-white text-purple-600 hover:bg-gray-100 rounded-full px-8"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Get Your Free Assessment
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default LuthorAILanding;